import {
  type Address,
  type GetBackupRestoresResponse,
  type InstallationFull,
  type InstallationUpdateRequest,
  type IntegrationResponse,
  type Retailer,
  type Subscriber,
} from '@common/types/api';
import { type DateTimeUTC, type DeviceId, type GUID } from '@common/types/brandedTypes';
import type AdditionalUser from '@common/types/models/AdditionalUser';
import type Contract from '@common/types/models/Contract';
import { type BaseDevice, createBaseDeviceFromResponse } from '@common/types/models/Device';
import type User from '@common/types/models/User';
import { parseTimestampAsUtc } from '@common/utils/datetime/isoTimestamp';

interface DeviceFull extends BaseDevice {
  readonly deviceId: DeviceId;
  readonly location: Address;
  readonly macAddress?: string;
  readonly firstIntrusion?: DateTimeUTC;
  readonly paired?: DateTimeUTC;
  readonly partners?: Retailer[];
  readonly isAlarmDeactivationByUserEnabled?: boolean;
  readonly ownerId?: GUID;
  readonly owner?: User;
  readonly additionalUsers?: AdditionalUser[];
  readonly notificationContacts?: Subscriber[];
  readonly comment?: string;
  readonly contract?: Contract;
  readonly retailerIds?: string[];
  readonly installationProfileId?: number;
  readonly model?: string;
  readonly operationManualUrl?: string;
  readonly cloudToken?: string;
  readonly backupRestores?: GetBackupRestoresResponse[];
  readonly integrations?: IntegrationResponse[];
}

export const createDeviceFullFromResponse = (apiDevice: InstallationFull): DeviceFull => ({
  ...createBaseDeviceFromResponse(apiDevice),
  deviceId: apiDevice.deviceId,
  location: apiDevice.address,
  macAddress: apiDevice.network?.macAddress,
  firstIntrusion: apiDevice.createdUtc ? parseTimestampAsUtc(apiDevice.createdUtc) : undefined,
  paired: apiDevice.pairedUtc ? parseTimestampAsUtc(apiDevice.pairedUtc) : undefined,
  ownerId: apiDevice.ownerId ? `${apiDevice.ownerId}` as GUID : undefined,
  isAlarmDeactivationByUserEnabled: apiDevice.isAlarmDeactivationByUserEnabled,
  retailerIds: apiDevice.retailerIds,
  installationProfileId: apiDevice.installationProfileId,
  model: apiDevice.model,
  operationManualUrl: apiDevice.operationManualUrl,
  cloudToken: apiDevice.cloudToken,
});

export const createDeviceUpdateRequest = (device: DeviceFull): InstallationUpdateRequest => ({
  id: device.id,
  name: device.name,
  address: device.location,
  model: device.model,
  operationManualUrl: device.operationManualUrl,
  timeZoneId: device.timeZoneId,
  retailerIds: device.retailerIds,
  installationProfileId: device.installationProfileId,
});

export default DeviceFull;
